import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PostList from './PostList';
import Loading from './icon/loading';
import Profileinfo from './Profileinfo';

const API_URL = process.env.REACT_APP_API_URL;

const ProfilePost = ({ user, onRefresh: onParentRefresh }) => {
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 5;

  const fetchUserPosts = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/get_posts.php?user_id=${user.id}`);
      const data = await res.json();

      if (data.success) {
        setUserPosts(data.posts || []);
        setCurrentPage(1);
      } else {
        console.error('Failed to load posts:', data.message);
      }
    } catch (err) {
      console.error('Error fetching posts:', err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUserPosts();
  }, [fetchUserPosts]);

  if (!user) {
    return (
      <p style={{ textAlign: 'center' }}>
        Please log in to view your profile posts.
      </p>
    );
  }

  const totalPages = Math.max(1, Math.ceil(userPosts.length / postsPerPage));
  const startIndex = (currentPage - 1) * postsPerPage;
  const currentPosts = userPosts.slice(startIndex, startIndex + postsPerPage);
  const repostCount = userPosts.filter((p) => p.post_type === 'repost').length;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRefresh = async () => {
    await fetchUserPosts();
    if (typeof onParentRefresh === 'function') {
      onParentRefresh();
    }
  };

  return (
    <div
      style={{
        width: '100%',
        maxWidth: 680,
        margin: '0 auto',
        padding: '0 8px',
        boxSizing: 'border-box',
      }}
    >
      <Profileinfo userId={user.id} />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          padding: '8px 0 4px',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 18, color: '#102a43' }}>
            My posts &amp; reposts
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
            {userPosts.length} post{userPosts.length === 1 ? '' : 's'}
            {repostCount > 0
              ? ` · ${repostCount} repost${repostCount === 1 ? '' : 's'}`
              : ''}
          </p>
        </div>
        <Link
          to="/home"
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: '#0a66c2',
            textDecoration: 'none',
          }}
        >
          Go to discussions →
        </Link>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center' }}>
          <Loading />
        </div>
      ) : userPosts.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6b7280',
          }}
        >
          <p style={{ marginBottom: 12 }}>
            You haven’t posted or reposted anything yet.
          </p>
          <Link
            to="/home"
            style={{ fontWeight: 600, color: '#0a66c2', textDecoration: 'none' }}
          >
            Browse discussions
          </Link>
        </div>
      ) : (
        <>
          <PostList
            posts={currentPosts}
            user={user}
            onReply={() => {}}
            onRefresh={handleRefresh}
            preserveOrder
            hidePagination
          />

          {totalPages > 1 && (
            <div
              className="pagination"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '20px',
                gap: '12px',
                fontFamily: 'Arial, sans-serif',
              }}
            >
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                style={{
                  padding: '10px 18px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: currentPage === 1 ? '#e0e0e0' : '#007bff',
                  color: '#fff',
                  fontWeight: '500',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                }}
              >
                &laquo; Prev
              </button>

              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#333',
                  padding: '8px 16px',
                  backgroundColor: '#f1f1f1',
                  borderRadius: '20px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}
              >
                Page {currentPage} of {totalPages}
              </span>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                style={{
                  padding: '10px 18px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor:
                    currentPage === totalPages ? '#e0e0e0' : '#007bff',
                  color: '#fff',
                  fontWeight: '500',
                  cursor:
                    currentPage === totalPages ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                }}
              >
                Next &raquo;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfilePost;
